import { useContext, useEffect, useRef, useState } from 'react';
import { ButtonBar } from 't-lib-ui/structure';
import { TextButton, InfiniteLoader, Note, Input } from 't-lib-ui/widgets';
import { FetchError, QueryStatus } from 't-lib-ui/connector';
import { nonNull } from 't-lib-ui/helpers';
import { RouterContext, goTo } from 't-lib-ui/router';
import { useGetAdminDuplicateFeeds, mergeAdminFeed, putAdminFeed, usePutAdminFeed, DuplicateGroupBy } from '../../connector/api';
import { AppState } from '../../context/general';
import { RestAdminDuplicateFeed, RestAdminDuplicateGroup } from '../../schema-copy/api-schema';
import { formatCompactDate } from '../../utils/formatting';
import { allDifferentSites, confusableNames, defaultKeeper, feedDomain, feedName, likelySameSets, mergeBlocker, nameWithDomain, sharedNames } from './duplicate-feeds';

const GROUPS_TO_SHOW = 20;

const tagStyle = (color: string): React.CSSProperties => ({
  marginLeft: '8px', padding: '1px 6px', fontSize: '0.75em', fontWeight: 600, color, border: `1px solid ${color}`, borderRadius: '4px',
});

const Dot = () => <span style={{ whiteSpace: 'nowrap' }}>•</span>;

interface FeedRowProps {
  feed: RestAdminDuplicateFeed;
  isKeeper: boolean;
  /** Set number when the feed is likely the same as others of the group (see likelySameSets);
   *  0 when there's just one such set (no number needed). */
  likelySame?: number;
  /** Another feed of the group has the same name although it's not the same feed: rename one. */
  confusableName?: boolean;
  magic: string;
  onKeep: () => void;
  /** The feed was marked as no duplicate (duplicateIgnored) and leaves the list; undefined when
   *  ignoring doesn't apply (the groups by name). */
  onIgnored?: () => void;
  /** The feed got a new shown name (its shortTitle; empty when cleared). */
  onRenamed: (shortTitle: string) => void;
}

const FeedRow: React.FC<FeedRowProps> = ({ feed, isKeeper, likelySame, confusableName, magic, onKeep, onIgnored, onRenamed }) => {
  const routerCtx = useContext(RouterContext);
  const putFeed = usePutAdminFeed(feed.id, magic);
  const [ignoring, setIgnoring] = useState(false);
  // The name being edited; undefined while not renaming.
  const [newName, setNewName] = useState<string>();
  const [renaming, setRenaming] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const editing = newName != undefined;
  useEffect(() => { if (editing) editorRef.current?.querySelector('input')?.focus(); }, [editing]);

  // Sets the shortTitle, which is the name readers see; the title comes from the feed itself and is
  // overwritten on every fetch. An empty name clears it, so the feed's own title shows again.
  const handleRename = async (name = newName) => {
    if (renaming || name == undefined) return;
    setRenaming(true);
    try {
      const updated = await putFeed({ shortTitle: name.trim() });
      onRenamed(updated.shortTitle ?? '');
      setNewName(undefined);
    } catch (e) {
      console.error('Rename failed', e);
      window.alert(e instanceof FetchError && e.message ? `Rename failed: ${e.message}` : 'Rename failed.');
    } finally {
      setRenaming(false);
    }
  };
  // Not a duplicate of the others (a different feed on the same site): leave the list for good.
  // Undone on the feed's admin page.
  const handleIgnore = async () => {
    setIgnoring(true);
    try {
      await putFeed({ duplicateIgnored: true });
      onIgnored?.();
    } catch (e) {
      console.error('Ignore failed', e);
      window.alert(e instanceof FetchError && e.message ? `Ignore failed: ${e.message}` : 'Ignore failed.');
      setIgnoring(false);
    }
  };
  const blocker = mergeBlocker(feed);
  const statusColor = feed.feedStatus == 'ACTIVE' ? 'var(--success-color)' : 'var(--error-color)';
  const lastPost = formatCompactDate(feed.lastNewPostPubDate);
  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--gray-850)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '240px' }}>
          <div style={{ fontWeight: 500 }}>
            {feedName(feed)}
            {confusableName && (
              <span style={tagStyle('var(--error-color)')} title="Another feed of this site has the same name but is not the same feed: rename one">
                SAME NAME
              </span>
            )}
            {likelySame != undefined && (
              <span style={tagStyle('var(--warning-color)')} title="Same name, post count and last post, or the same URL apart from http/https, a trailing slash or /atom: most likely the same feed">
                LIKELY SAME{likelySame ? ` ${likelySame}` : ''}
              </span>
            )}
            {feed.hasCatalog && <span style={tagStyle('var(--dr-link-blue)')}>CATALOG</span>}
            {isKeeper && <span style={tagStyle('var(--success-color)')}>KEEP</span>}
          </div>
          <div style={{ fontSize: '0.85em', color: 'var(--gray-400)', wordBreak: 'break-all' }}>{feed.url}</div>
          <div style={{ fontSize: '0.85em', color: 'var(--gray-400)', display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
            <span style={{ color: statusColor }}>{feed.feedStatus}</span>
            <Dot /><span>{feed.subscriberCount} sub{feed.subscriberCount == 1 ? '' : 's'}</span>
            <Dot /><span>{feed.postCount} post{feed.postCount == 1 ? '' : 's'}</span>
            <Dot /><span>since {formatCompactDate(feed.createdAt)}</span>
            {lastPost && <><Dot /><span>last post {lastPost}</span></>}
            {feed.manuallyVerified && !feed.hasCatalog && <><Dot /><span style={{ color: 'var(--success-color)' }}>Verified</span></>}
            {!isKeeper && blocker && <><Dot /><span style={{ color: 'var(--error-color)' }}>can't merge: {blocker}</span></>}
          </div>
          {!!feed.aliases.length && (
            <div style={{ fontSize: '0.8em', color: 'var(--gray-500)', marginTop: '4px', wordBreak: 'break-all' }}>
              Aliases: {feed.aliases.join(', ')}
            </div>
          )}
        </div>
        <ButtonBar size="small">
          <TextButton label="Open" focussable="flow" onClick={() => goTo(routerCtx, `/a/admin/feed/${feed.id}`)} />
          <TextButton label="View" focussable="sideway" onClick={() => window.open(`/a/feed/${feed.id}`, '_blank', 'noopener')} />
          <TextButton label={isKeeper ? 'Kept' : 'Keep'} type={isKeeper ? 'major' : 'minor'} focussable="sideway" onClick={onKeep} />
          <TextButton label="Rename" type="minor" focussable="sideway" onClick={() => setNewName(feedName(feed))} />
          {onIgnored && <TextButton label="Ignore" type="minor" focussable="sideway" busy={ignoring} onClick={handleIgnore} />}
        </ButtonBar>
      </div>
      {editing && (
        <div ref={editorRef} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', maxWidth: '700px' }}
          onKeyDown={e => { if (e.key == 'Escape') { e.stopPropagation(); setNewName(undefined); } }}>
          <div style={{ flex: 1 }}>
            <Input value={newName} onInput={setNewName} onEnter={() => handleRename()} placeholder={`Empty: "${feed.title}"`}
              maxLength={200} layout="singleCol" borderless={false} />
          </div>
          <ButtonBar size="small">
            <TextButton label="Save" focussable="flow" busy={renaming} onClick={() => handleRename()} />
            {feedDomain(feed) && <TextButton label="Add Domain" type="minor" focussable="sideway"
              enabled={nameWithDomain(feed, newName) ? 'enabled' : 'disabled'}
              onClick={() => handleRename(nameWithDomain(feed, newName))} />}
            <TextButton label="Cancel" type="minor" focussable="sideway" onClick={() => setNewName(undefined)} />
          </ButtonBar>
        </div>
      )}
    </div>
  );
};

interface GroupProps {
  group: RestAdminDuplicateGroup;
  by: DuplicateGroupBy;
  magic: string;
  /** Feeds of this group were merged away or ignored: they leave the list. */
  onRemoved: (feedIds: string[]) => void;
}

const DuplicateGroup: React.FC<GroupProps> = ({ group, by, magic, onRemoved }) => {
  const appState = useContext(AppState);
  const [keeperId, setKeeperId] = useState(() => defaultKeeper(group.feeds).id);
  // New names given here (shortTitle by feed id), shown without reloading the list.
  const [renamed, setRenamed] = useState<Map<string, string>>(new Map());
  const feeds = group.feeds.map(f => renamed.has(f.id) ? { ...f, shortTitle: renamed.get(f.id) || undefined } : f);
  const [busy, setBusy] = useState(false);
  // The chosen keeper may have been ignored: then the default among the rest.
  const keeper = feeds.find(f => f.id == keeperId) ?? defaultKeeper(feeds);
  const inCatalog = feeds.filter(f => f.hasCatalog).length;
  const sameSets = likelySameSets(feeds);
  const confusable = confusableNames(feeds, sameSets);
  const numberSets = new Set(sameSets.values()).size > 1;
  // Feeds on different sites are most likely different feeds: offer adding each one's domain to its
  // name instead. Merging stays for those that are likely the same feed anyway, and only for the
  // keeper's set of them.
  const differentSites = allDifferentSites(feeds);
  const shared = sharedNames(feeds);
  const showMerge = !differentSites || !!sameSets.size;
  const mergeable = feeds.filter(f => f.id != keeper.id && !mergeBlocker(f)
    && (!differentSites || (sameSets.has(f.id) && sameSets.get(f.id) == sameSets.get(keeper.id))));
  const renames = differentSites ? feeds.filter(f => shared.has(f.id)).flatMap(f => { const name = nameWithDomain(f); return name ? [{ feed: f, name }] : []; }) : [];

  const handleRenameAll = async () => {
    if (busy || !renames.length) return;
    setBusy(true);
    try {
      for (const { feed, name } of renames) {
        const updated = await putAdminFeed(nonNull(appState.state.authData), feed.id, { shortTitle: name }, magic);
        setRenamed(prev => new Map(prev).set(feed.id, updated.shortTitle ?? ''));
      }
    } catch (e) {
      console.error('Rename failed', e);
      window.alert(e instanceof FetchError && e.message ? `Rename failed: ${e.message}` : 'Rename failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleMerge = async () => {
    if (busy || !mergeable.length) return;
    if (!window.confirm(`Merge ${mergeable.map(f => `"${feedName(f)}" (${f.url})`).join(', ')} into "${feedName(keeper)}" (${keeper.url})?\n\n` +
        'Their aliases move to the kept feed, then they are deleted with all their content.'))
      return;
    setBusy(true);
    const merged: string[] = [];
    try {
      for (const feed of mergeable) {
        await mergeAdminFeed(nonNull(appState.state.authData), feed.id, keeper.id, magic);
        merged.push(feed.id);
      }
    } catch (e) {
      console.error('Merge failed', e);
      window.alert(e instanceof FetchError && e.message ? `Merge failed: ${e.message}` : 'Merge failed.');
    } finally {
      setBusy(false);
      if (merged.length) onRemoved(merged);
    }
  };

  return (
    <div style={{ margin: '16px 0', padding: '12px 16px', border: '1px solid var(--dr-border-panel)', borderRadius: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '6px' }}>
        <div style={{ flex: 1, fontWeight: 600, wordBreak: 'break-all' }}>
          {by == 'name' ? feedName(group.feeds[0])
            : <a href={`https://${group.siteKey}`} target="_blank" rel="noopener noreferrer">{group.siteKey}</a>}
          <span style={{ marginLeft: '8px', fontWeight: 400, fontSize: '0.85em', color: 'var(--gray-500)' }}>{group.feeds.length} feeds</span>
          <span style={{ marginLeft: '8px', fontWeight: 400, fontSize: '0.85em', color: inCatalog ? 'var(--dr-link-blue)' : 'var(--gray-500)' }}>
            {inCatalog ? (inCatalog == feeds.length ? `all ${inCatalog} in catalog` : `${inCatalog} in catalog`) : 'none in catalog'}
          </span>
        </div>
        <ButtonBar size="small">
          {!!renames.length && <TextButton
            label={`Rename all: add domain to ${renames.length}`}
            focussable="flow"
            busy={busy}
            onClick={handleRenameAll}
          />}
          {showMerge && <TextButton
            label={mergeable.length ? `Merge ${mergeable.length} into "${feedName(keeper)}"` : 'Nothing to merge'}
            focussable={differentSites ? 'sideway' : 'flow'}
            enabled={mergeable.length ? 'enabled' : 'disabled'}
            busy={busy}
            onClick={handleMerge}
          />}
        </ButtonBar>
      </div>
      {feeds.map(feed => (
        <FeedRow key={feed.id} feed={feed} isKeeper={showMerge && feed.id == keeper.id} magic={magic} confusableName={confusable.has(feed.id)}
          onRenamed={name => setRenamed(prev => new Map(prev).set(feed.id, name))}
          likelySame={sameSets.has(feed.id) ? (numberSets ? sameSets.get(feed.id) : 0) : undefined}
          onKeep={() => setKeeperId(feed.id)} onIgnored={by == 'site' ? () => onRemoved([feed.id]) : undefined} />
      ))}
    </div>
  );
};

/** Admin list of public feeds that point to the same site (or have the same shown name), with
 *  merging and renaming. Newest group first. */
const AdminDuplicateFeeds: React.FC<{ by: DuplicateGroupBy, magic: string }> = ({ by, magic }) => {
  const { chunks, status, hasMore, loadMore } = useGetAdminDuplicateFeeds(GROUPS_TO_SHOW, by, magic);
  // Merged and ignored feeds are gone from the server's list; drop them here instead of reloading.
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  const groups = (chunks ?? []).flatMap(chunk => chunk.groups)
    .map(g => ({ ...g, feeds: g.feeds.filter(f => !removedIds.has(f.id)) }))
    .filter(g => g.feeds.length > 1);

  return (
    <div style={{ maxWidth: '1500px' }}>
      {by == 'name' ? <Note type="tiny">
        Public feeds with the same shown name (short title, else title; ignoring case), newest first.
        Readers can't tell them apart: rename one, or merge them if they are the same feed. Merging
        moves a feed's aliases to the kept feed and deletes it; only feeds without subscribers,
        catalog entry and starred/read-later posts can be merged away.
      </Note> : <Note type="tiny">
        Public feeds whose site URL is the same (ignoring http/https, www. and a trailing slash), newest first.
        Merging moves a feed's aliases to the kept feed and deletes it; only feeds without subscribers,
        catalog entry and starred/read-later posts can be merged away. Ignore a feed that is not a
        duplicate (another feed of the same site): it leaves this list until undone on its admin page.
      </Note>}
      {groups.map(group => (
        <DuplicateGroup key={group.siteKey} group={group} by={by} magic={magic}
          onRemoved={ids => setRemovedIds(prev => new Set([...Array.from(prev), ...ids]))} />
      ))}
      {status == QueryStatus.Success && !hasMore && !groups.length && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>{by == 'name' ? 'No feeds with the same name found.' : 'No duplicate feeds found.'}</div>
      )}
      {status == QueryStatus.Error && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--error-color)' }}>Error loading duplicate feeds!</div>
      )}
      <div style={{ textAlign: 'center' }}>
        <InfiniteLoader active={hasMore} onActivation={loadMore} />
      </div>
    </div>
  );
};

export default AdminDuplicateFeeds;
